import { useEffect, useState } from 'react';
import { DragDropProvider, useDroppable } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { useProjectRoles } from '../../../hooks/useProjectRoles';
import { useTagForm, useSaveTagForm, makeTagElement, makeTextElement, isBlankElement, TagForm, TagFormElement, TagFormAvailableTag } from '../../../hooks/useTagForm';
import { TagModal } from '../../../components/modals/TagModal';
import type { Tag } from '../../../hooks/useProjectTags';
import { useProject } from '../../../hooks/useProject';
import { LoadingPlaceholder } from '../../../components/LoadingPlaceholder';
import { SortableElement } from './SortableElement';
import { LeftPanel } from './LeftPanel';

const FORM_DROP_ID = 'tag-form-area';

// Drops other elements for the same inverse pair (e.g. a "Not" checkbox once "Hot" gains it as inverse).
function withoutInverseDuplicates(elements: TagFormElement[], kept: TagFormElement): TagFormElement[] {
  const inverseId = kept.inverseTag?.id;
  if (!inverseId) return elements;
  const pair = [kept.tagId, inverseId];
  return elements.filter(el => el === kept || !(pair.includes(el.tagId) || pair.includes(el.inverseTag?.id)));
}

function DroppableFormArea({ children, isEmpty }: { children: React.ReactNode; isEmpty: boolean }) {
  const { ref, isDropTarget } = useDroppable({ id: FORM_DROP_ID });
  return (
    <div
      ref={ref}
      className="admin-tag-form"
      data-testid="admin-forms-drop-panel"
      style={isDropTarget ? { outline: '2px dashed #337ab7', borderRadius: 4 } : undefined}
    >
      {isEmpty && (
        <h4 className="text-muted">
          <i className="fas fa-arrow-down" aria-hidden="true" /> Drop Form Component or Tag to get started
        </h4>
      )}
      {children}
    </div>
  );
}

export function FormsTab({ projectId }: { projectId: string }) {
  const { data: project } = useProject(projectId);
  const { data: roles = [], isLoading: loadingRoles } = useProjectRoles(projectId);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [localForm, setLocalForm] = useState<TagForm | null>(null);
  const [showTagModal, setShowTagModal] = useState(false);

  useEffect(() => {
    if (roles.length > 0 && !selectedRoleId) setSelectedRoleId(roles[0].id);
  }, [roles]);

  const { data: tagForm, isLoading: loadingForm } = useTagForm(projectId, selectedRoleId);
  const saveTagForm = useSaveTagForm(projectId);

  useEffect(() => {
    if (tagForm) setLocalForm(tagForm);
  }, [tagForm]);

  const save = (elements: TagFormElement[]) => {
    if (!localForm) return;
    const updated = { ...localForm, elements };
    setLocalForm(updated);
    if (elements.some(isBlankElement)) return;
    saveTagForm.mutate({ ...updated, saving: true });
  };

  const addComponent = (type: 'radio' | 'checkbox' | 'text') => {
    if (!localForm) return;
    save([...localForm.elements, type === 'text' ? makeTextElement() : makeTagElement({ type })]);
  };

  const addTagElement = ({ tag, inverseTag, type }: TagFormAvailableTag) => {
    if (!localForm) return;
    save([...localForm.elements, makeTagElement({ type, tagId: tag.id, tag, ...(inverseTag ? { inverseTag } : {}) })]);
  };

  const updateElement = (index: number, updated: TagFormElement) => {
    if (!localForm) return;
    save(withoutInverseDuplicates(localForm.elements.map((el, i) => i === index ? updated : el), updated));
  };

  const removeElement = (index: number) => {
    if (!localForm) return;
    save(localForm.elements.filter((_, i) => i !== index));
  };

  const handleAddTag = () => setShowTagModal(true);

  const handleTagSaved = ({ inverseObjects, childrenObjects, ...tag }: Tag & { inverseObjects: { id: string; name: string }[]; childrenObjects: { id: string; name: string }[] }) => {
    if (!localForm) return;
    const type = inverseObjects?.length ? 'radio' : 'checkbox';
    const element = makeTagElement({ type, tagId: tag.id, tag, children: childrenObjects as Tag[], ...(inverseObjects?.[0] ? { inverseTag: inverseObjects[0] as Tag } : {}) });
    save(withoutInverseDuplicates([element, ...localForm.elements], element));
    setShowTagModal(false);
  };

  if (loadingRoles) return <LoadingPlaceholder />;

  return (
    <div>
      <h2><i className="fas fa-edit" aria-hidden="true" /> Tagging Forms</h2>

      {loadingForm || !localForm ? <LoadingPlaceholder label="Loading form…" /> : (
        <DragDropProvider
          onDragEnd={({ canceled, operation }) => {
            if (canceled) return;
            if (isSortable(operation.source)) {
              const { initialIndex, index } = operation.source.sortable;
              if (initialIndex === index) return;
              const next = [...localForm.elements];
              const [moved] = next.splice(initialIndex, 1);
              next.splice(index, 0, moved);
              save(next);
            } else if (operation.target?.id === FORM_DROP_ID) {
              const { palette, entry, componentType } = (operation.source.data ?? {}) as Record<string, any>;
              if (palette && entry) addTagElement(entry);
              else if (palette && componentType) addComponent(componentType);
            }
          }}
        >
          <div className="row">
            <div className="col-sm-2">
              <LeftPanel
                project={project}
                tagForm={localForm}
                onAddComponent={addComponent}
                onAddTag={handleAddTag}
                onAddTagElement={entry => addTagElement(entry)}
              />
            </div>

            <div className="col-sm-10">
              <div className="well well-lg">
                <h5>Tagging Form</h5>
                <div className="form-group">
                  <div className="row">
                    <div className="col-sm-6">
                      <select
                        required
                        className="form-control"
                        data-testid="admin-forms-role-select"
                        value={selectedRoleId}
                        onChange={e => setSelectedRoleId(e.target.value)}
                      >
                        {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <DroppableFormArea isEmpty={localForm.elements.length === 0}>
                  {localForm.elements.map((el, i) => (
                    <SortableElement
                      key={el.id ?? `new-${i}`}
                      element={el}
                      index={i}
                      projectId={projectId}
                      onSave={updated => updateElement(i, updated)}
                      onRemove={() => removeElement(i)}
                    />
                  ))}
                </DroppableFormArea>
              </div>
            </div>
          </div>
        </DragDropProvider>
      )}
      {showTagModal && project && (
        <TagModal projectId={projectId} onClose={() => setShowTagModal(false)} onSaved={handleTagSaved} />
      )}
    </div>
  );
}
